import {useState} from 'react';

/**
 * Computes a safe page number that prevents race conditions when currency, country, or selectedFilterPresetId changes.
 *
 * When currency, country, or selectedFilterPresetId changes, the page should be reset to 1. However, the page reset
 * happens asynchronously via useEffect in useSourcingTable, which can cause queries to run
 * with the new currency/country/filterPreset but the old page number before the reset completes.
 *
 * This hook ensures that when currency, country, or selectedFilterPresetId changes, the returned page is always 1
 * until the actual page state is reset to 1, preventing queries with mismatched parameters.
 */
export function useSourcingSafePage(
  currency: string | undefined,
  country: string | undefined,
  selectedFilterPresetId: string | undefined | null,
  currentPage: number | null
): number {
  const [prevCurrency, setPrevCurrency] = useState(currency);
  const [prevCountry, setPrevCountry] = useState(country);
  const [prevSelectedFilterPresetId, setPrevSelectedFilterPresetId] =
    useState(selectedFilterPresetId);
  const [pendingReset, setPendingReset] = useState(false);

  // React supports conditional setState during render for prop changes
  if (
    prevCurrency !== currency ||
    prevCountry !== country ||
    prevSelectedFilterPresetId !== selectedFilterPresetId
  ) {
    setPrevCurrency(currency);
    setPrevCountry(country);
    setPrevSelectedFilterPresetId(selectedFilterPresetId);
    setPendingReset(true);
  }

  if (pendingReset && currentPage === 1) {
    setPendingReset(false);
  }

  return pendingReset ? 1 : (currentPage ?? 1);
}
